import { atom, createStore } from 'jotai';
import {
  PanelDisplayStatus,
  DisplayTask,
  TaskId,
  ExpectedPrepareResponse,
  DefaultTaskDescription,
  Task,
} from './@types';

//#region data store
const _panelDisplayStatusAtom = atom<PanelDisplayStatus>({
  isOpen: false,
  isFold: false,
});
_panelDisplayStatusAtom.debugLabel = 'panelDisplayStatusAtom';
const _tasksAtom = atom<Map<TaskId, DefaultTaskDescription>>(new Map());
_tasksAtom.debugLabel = 'tasksAtom';
//#endregion

//#region - GETTERS
export const panelStatusAtom = atom(
  (get) => {
    return get(_panelDisplayStatusAtom);
  },
  (get, set, updateFn: (prev: PanelDisplayStatus) => PanelDisplayStatus) => {
    const prev = get(_panelDisplayStatusAtom);
    set(_panelDisplayStatusAtom, updateFn(prev));
  }
);

export const displayTasksAtom = atom<DisplayTask<ExpectedPrepareResponse, unknown, unknown, unknown>[]>((get) => {
  return Array.from(get(_tasksAtom).values()).map((task) => {
    return {
      taskId: task.taskId,
      processId: task.processId,
      fileName: task.fileName,
      fileType: task.fileType,
      progress: task.progress,
      status: task.status,
      removeFn: task.removeFn,
      onRemoved: task.onTaskRemoved,
      onClick: task.onTaskClick
        ? (taskId: number) => {
            task.onTaskClick?.({
              taskId,
              fileName: task.fileName,
              fileType: task.fileType,
              status: task.status,
              processId: task.processId,
              result: task.result,
            });
          }
        : undefined,
    };
  });
});

export const prepareTaskAtom = atom((get) => {
  return Array.from(get(_tasksAtom).values())
    .filter((task) => task.status === 'prepare')
    .map((task) => ({
      taskId: task.taskId,
      processId: task.processId,
      fileName: task.fileName,
      fileType: task.fileType,
      status: task.status,

      prepareFn: task.prepareFn,
      removeFn: task.removeFn,
      onPrepared: task.onPrepared,
      onFailed: task.onFailed,
    }));
});

export const pollingTaskAtom = atom((get) => {
  return Array.from(get(_tasksAtom).values())
    .filter((task) => task.status === 'processing')
    .map((task) => ({
      taskId: task.taskId,
      processId: task.processId,
      fileName: task.fileName,
      fileType: task.fileType,
      status: task.status,

      pollingFn: task.pollingFn,
      pollingInterval: task.pollingInterval,
      removeFn: task.removeFn,
      onProgress: task.onProgress,
      onCompleted: task.onCompleted,
      onFailed: task.onFailed,
    }));
});

export const tasksStatusAtom = atom((get) => {
  return Array.from(get(_tasksAtom).values()).map((task) => ({
    taskId: task.taskId,
    status: task.status,
  }));
});

export const labelAtom = atom<{ title: string; status: Task['status'] }>((get) => {
  const tasks = get(_tasksAtom);

  let [totalCount, preparingCount, exportingCount, completedCount, failedCount] = [0, 0, 0, 0, 0];
  for (const task of tasks.values()) {
    if (task.status === 'prepare') {
      preparingCount++;
    } else if (task.status === 'processing') {
      exportingCount++;
    } else if (task.status === 'completed') {
      completedCount++;
    } else if (task.status === 'failed') {
      failedCount++;
    }
    totalCount++;
  }

  if (totalCount > 0) {
    if (failedCount > 0) {
      return {
        title: 'Export failed',
        status: 'failed',
      };
    } else if (exportingCount > 0) {
      return {
        title: 'Exporting',
        status: 'processing',
      };
    } else if (preparingCount > 0) {
      return {
        title: 'Preparing export',
        status: 'prepare',
      };
    } else if (completedCount > 0) {
      return {
        title: 'Export complete',
        status: 'completed',
      };
    }
  }
  return {
    title: 'Export complete',
    status: 'completed',
  };
});
//
//#endregion

//#region - SETTERS
export const addTaskAtom = atom(null, (_, set, param: DefaultTaskDescription) => {
  set(_tasksAtom, (prev) => {
    const newTasks = new Map(prev);
    newTasks.set(param.taskId, param);
    return newTasks;
  });
  set(_panelDisplayStatusAtom, { isOpen: true, isFold: false });
});
export const updateTaskStatusAtom = atom(
  null,
  (get, set, param: Pick<DefaultTaskDescription, 'taskId' | 'processId' | 'progress' | 'status'>) => {
    set(_tasksAtom, (prev) => {
      const newTasks = new Map(prev);
      const task = prev.get(param.taskId);
      if (task) {
        newTasks.set(param.taskId, {
          ...task,
          status: param.status,
          progress: param.progress ?? task.progress,
          processId: param.processId,
        });
      }
      return newTasks;
    });
  }
);
export const removeTaskAtom = atom(null, (get, set, param: Pick<DefaultTaskDescription, 'taskId'>) => {
  const prev = get(_tasksAtom);
  const newTasks = new Map(prev);
  newTasks.delete(param.taskId);
  set(_tasksAtom, newTasks);

  if (newTasks.size === 0) {
    set(_panelDisplayStatusAtom, { isOpen: false, isFold: false });
  }

  return newTasks;
});
export const updateTaskResultAtom = atom(null, (get, set, param: Pick<DefaultTaskDescription, 'taskId' | 'result'>) => {
  set(_tasksAtom, (prev) => {
    const newTasks = new Map(prev);
    const task = prev.get(param.taskId);
    if (task) {
      newTasks.set(param.taskId, {
        ...task,
        result: { ...(task.result || {}), ...(param.result || {}) },
      });
    }
    return newTasks;
  });
});
//#endregion

// Provider로 감싸여진 영역 대응을 위한 Store
export const downloadPanelTaskStore = createStore();
downloadPanelTaskStore.sub(_panelDisplayStatusAtom, () => {});
downloadPanelTaskStore.sub(_tasksAtom, () => {});
// downloadPanelTaskStore.sub(pollingTaskAtom, () => {});
